import type { GuidanceFieldFeatures } from 'api-validations/geojson-schemas'
import type { Locutus } from 'api-validations/locutus'
import {
  DEFAULT_GLOBAL_SETTINGS,
  type GuidancePlan,
  GuidancePrimitive,
  PropagationMode,
  type PropagatableGuidancePrimitive,
  type RegionBoundedHelmRequest
} from 'api-validations/locutus/guidance'

export const NO_PROPAGATION_REQUEST: RegionBoundedHelmRequest = {
  type: PropagationMode.RegionBounded,
  fieldFeatures: { type: 'FeatureCollection', features: [] },
  globalSettings: DEFAULT_GLOBAL_SETTINGS,
  overrides: {},
  infillRegions: []
}

const isPropagatable = (
  primitive: Locutus.GuidancePrimitiveAsset['data']
): primitive is PropagatableGuidancePrimitive => primitive.type !== GuidancePrimitive.PrePropagated

/**
 * Helm matches `infillRegions[].regionId` against each boundary feature's
 * top-level `id`, while inspection reports a feature's region under
 * `properties.region`. Lift it, dropping the features that belong to no region
 * (edges, corners, holes).
 */
export const toPropagationBoundary = (
  fieldFeatures: GuidanceFieldFeatures
): GuidanceFieldFeatures => ({
  type: 'FeatureCollection',
  features: fieldFeatures.features.flatMap((feature) => {
    const region = feature.properties.region
    return typeof region === 'string' && region !== '' ? [{ ...feature, id: region }] : []
  })
})

// Saved primitives carry the swath width they were drawn with; the plan's
// implement width is what the display will actually propagate at.
export const withSwathWidth = <T extends PropagatableGuidancePrimitive>({
  infill,
  swathWidthFt
}: {
  infill: T
  swathWidthFt: number
}): T => ({
  ...infill,
  primitive: {
    ...infill.primitive,
    properties: { guidance: { ...infill.primitive.properties.guidance, swathWidthFt } }
  }
})

/**
 * The request that reproduces a saved guidance plan: every region whose line
 * is still a field asset, at the plan's implement width, under the plan's
 * settings and overrides. `null` when nothing in the plan can propagate.
 */
export const buildPlanPropagationRequest = ({
  plan,
  primitives
}: {
  plan: GuidancePlan
  primitives: Pick<Locutus.GuidancePrimitiveAsset, 'id' | 'data'>[]
}): RegionBoundedHelmRequest | null => {
  const propagationBoundary = toPropagationBoundary(plan.fieldFeatures)
  const regionIds = new Set(propagationBoundary.features.map((feature) => feature.id))
  const primitiveById = new Map(primitives.map((primitive) => [primitive.id, primitive]))

  const infillRegions = plan.infillRegions.flatMap(({ regionId, infill }) => {
    const primitive = primitiveById.get(infill.id)?.data
    if (primitive === undefined || !isPropagatable(primitive)) return []
    if (regionId === undefined || !regionIds.has(regionId)) return []
    return [
      {
        regionId,
        infill: withSwathWidth({ infill: primitive, swathWidthFt: plan.implementWidth })
      }
    ]
  })
  if (infillRegions.length === 0) return null

  return {
    type: PropagationMode.RegionBounded,
    fieldFeatures: propagationBoundary,
    globalSettings: plan.globalSettings,
    overrides: plan.overrides,
    infillRegions
  }
}
